
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader2, Sparkles, ChevronLeft } from 'lucide-react';

interface LoginProps {
  onLogin: (email: string, pass: string) => Promise<boolean>;
  isAuth: boolean;
}

const Login: React.FC<LoginProps> = ({ onLogin, isAuth }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuth) {
      navigate('/admin');
    }
  }, [isAuth, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsLoading(true);

    try {
      await onLogin(email, password);
    } catch (err: any) {
      setErrorMsg('Accès refusé. Vérifiez vos identifiants.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-offwhite px-6">
      {/* Carte en Beige/Marron clair */}
      <div className="max-w-md w-full bg-brand-cream p-12 shadow-[0_32px_64px_-16px_rgba(62,50,44,0.1)] rounded-[2.5rem] border border-brand-black/5 animate-in fade-in zoom-in duration-700">
        <div className="text-center mb-12">
          <div className="inline-flex p-5 bg-brand-offwhite rounded-full mb-8 text-brand-gold animate-pulse shadow-sm border border-brand-black/5">
            <Sparkles size={32} />
          </div>
          <h1 className="font-serif text-3xl mb-3 tracking-tight text-brand-black">Espace Directoire</h1>
          <p className="text-[10px] uppercase tracking-[0.4em] text-brand-black/40 font-bold">R & C Essentials Admin</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-[9px] uppercase tracking-widest font-bold text-brand-black/40 ml-4">Email Professionnel</label>
            <div className="relative">
              <Mail size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-gold/60" />
              <input 
                type="email"
                className="w-full p-5 pl-14 bg-brand-offwhite border border-brand-black/5 rounded-2xl outline-none focus:border-brand-gold text-sm text-brand-black placeholder:text-brand-black/30"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nom@rc-essentials.com"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-[9px] uppercase tracking-widest font-bold text-brand-black/40 ml-4">Mot de passe</label>
            <div className="relative">
              <Lock size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-gold/60" />
              <input 
                type="password"
                className="w-full p-5 pl-14 bg-brand-offwhite border border-brand-black/5 rounded-2xl outline-none focus:border-brand-gold text-sm text-brand-black placeholder:text-brand-black/30"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoading}
              />
            </div>
            {errorMsg && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <p className="text-red-500 text-[10px] mt-6 uppercase tracking-widest font-bold text-center bg-red-50 p-4 rounded-xl border border-red-100">
                  {errorMsg}
                </p>
              </div>
            )}
          </div>

          <button 
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-black text-brand-offwhite py-5 rounded-2xl text-[11px] uppercase tracking-[0.4em] font-bold hover:bg-brand-gold hover:text-white transition-all shadow-xl active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 mt-4"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : "Se connecter"}
          </button>
        </form>

        <div className="mt-12 text-center">
          <button 
            onClick={() => navigate('/')} 
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-brand-black/40 hover:text-brand-gold transition-colors"
          >
            <ChevronLeft size={12} /> Retour au site public
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
